<div class="chat-panel" [class.fullscreen]="isFullscreen" data-testid="chat-panel">
  <!-- Header -->
  <div class="chat-header">
    <div class="chat-header-title">
      <mat-icon svgIcon="genai" class="chat-genai-icon"></mat-icon>
      <span>Assistant</span>
    </div>
    <div class="chat-header-actions">
      <button mat-icon-button *ngIf="messages.length > 0" (click)="newConversation()" matTooltip="New chat">
        <mat-icon>add</mat-icon>
      </button>
      <button mat-icon-button (click)="toggleFullscreen()" [matTooltip]="isFullscreen ? 'Side panel' : 'Full screen'">
        <mat-icon [svgIcon]="isFullscreen ? 'fullscreen-collapse' : 'fullscreen-expand'"></mat-icon>
      </button>
      <button mat-icon-button (click)="close()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- AI Consent Banner -->
  <div *ngIf="consentChecked && !consentAccepted" class="chat-consent-overlay">
    <div class="chat-consent-card">
      <mat-icon class="chat-consent-icon">security</mat-icon>
      <h3>AI Data Processing Disclosure</h3>
      <p>
        The Project Assistant uses <strong>Anthropic's Claude AI</strong> to answer your questions.
        When you use this feature, your project data (daily reports, bid items, submittals, RFIs,
        issues, pay apps, etc.) will be sent to Anthropic's API for processing.
      </p>
      <ul>
        <li>Data is sent via encrypted API calls</li>
        <li>Anthropic does not use API data for model training</li>
        <li>Data is retained by Anthropic for up to 30 days for safety</li>
      </ul>
      <p class="chat-consent-note">
        By accepting, you acknowledge that project data will be processed by a third-party AI service.
      </p>
      <div class="chat-consent-actions">
        <button mat-stroked-button (click)="close()">Decline</button>
        <button mat-flat-button color="primary" (click)="acceptConsent()" [disabled]="consentLoading">
          {{ consentLoading ? 'Accepting...' : 'Accept & Continue' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Project picker -->
  <div *ngIf="needsProjectSelection && projectResolved" class="chat-messages">
    <div class="chat-center-col">
      <div class="chat-empty">
        <p class="chat-empty-title">Select a project</p>
        <p class="chat-empty-subtitle">Which project would you like to ask about?</p>
        <div class="chat-suggestions">
          <button *ngFor="let p of availableProjects"
                  class="chat-pill"
                  (click)="selectProject(p)">
            {{p.name}}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Messages -->
  <div *ngIf="!needsProjectSelection && consentAccepted && projectResolved" class="chat-messages" #messageContainer>
    <div class="chat-center-col">
      <!-- Tab bar + content when no active conversation -->
      <div *ngIf="messages.length === 0 && !isLoading" class="chat-tabs-container">
        <!-- Tab bar -->
        <div class="chat-tab-bar">
          <button class="chat-tab" [class.active]="activeTab === 'new'" (click)="activeTab = 'new'">New</button>
          <button class="chat-tab" [class.active]="activeTab === 'history'" (click)="switchToHistory()">History</button>
        </div>

        <!-- New tab content -->
        <div *ngIf="activeTab === 'new'" class="chat-empty">
          <p class="chat-empty-title" data-testid="chat-empty-title">How can I help?</p>
          <div class="chat-suggestions">
            <button *ngFor="let q of suggestedQuestions" class="chat-pill" (click)="askQuestion(q)">{{q}}</button>
          </div>
        </div>

        <!-- History tab content -->
        <div *ngIf="activeTab === 'history'" class="chat-history">
          <!-- Filter chips -->
          <div class="chat-filter-chips">
            <button class="chat-filter-chip" [class.active]="historyFilter === 'all'" (click)="historyFilter = 'all'">All</button>
            <button class="chat-filter-chip" [class.active]="historyFilter === 'favorites'" (click)="historyFilter = 'favorites'">&#9733; Favorites</button>
          </div>

          <!-- Favorites (pinned) section - show in "all" filter when there are favorites -->
          <div *ngIf="historyFilter === 'all' && pinnedConversations.length > 0">
            <div class="chat-section-label">Pinned</div>
            <div class="chat-conv-list">
              <button *ngFor="let conv of pinnedConversations" class="chat-conv-item" (click)="loadConversation(conv.uuid)">
                <div class="chat-conv-icon"><mat-icon>chat_bubble_outline</mat-icon></div>
                <div class="chat-conv-text">
                  <div class="chat-conv-title">{{conv.title}}</div>
                  <div class="chat-conv-meta">{{conv.updated_at | date:'mediumDate'}} <span class="chat-msg-count">{{conv.message_count || 0}} msgs</span></div>
                </div>
                <button class="chat-conv-star active" (click)="toggleConversationFavorite(conv, $event)">&#9733;</button>
              </button>
            </div>
          </div>

          <!-- Recent section -->
          <div *ngIf="historyFilter === 'all' && recentConversations.length > 0">
            <div class="chat-section-label">Recent</div>
            <div class="chat-conv-list">
              <button *ngFor="let conv of recentConversations" class="chat-conv-item" (click)="loadConversation(conv.uuid)">
                <div class="chat-conv-icon"><mat-icon>chat_bubble_outline</mat-icon></div>
                <div class="chat-conv-text">
                  <div class="chat-conv-title">{{conv.title}}</div>
                  <div class="chat-conv-meta">{{conv.updated_at | date:'mediumDate'}} <span class="chat-msg-count">{{conv.message_count || 0}} msgs</span></div>
                </div>
                <button class="chat-conv-star" (click)="toggleConversationFavorite(conv, $event)">&#9734;</button>
              </button>
            </div>
          </div>

          <!-- Favorites only filter -->
          <div *ngIf="historyFilter === 'favorites'">
            <div class="chat-conv-list">
              <button *ngFor="let conv of pinnedConversations" class="chat-conv-item" (click)="loadConversation(conv.uuid)">
                <div class="chat-conv-icon"><mat-icon>chat_bubble_outline</mat-icon></div>
                <div class="chat-conv-text">
                  <div class="chat-conv-title">{{conv.title}}</div>
                  <div class="chat-conv-meta">{{conv.updated_at | date:'mediumDate'}} <span class="chat-msg-count">{{conv.message_count || 0}} msgs</span></div>
                </div>
                <button class="chat-conv-star active" (click)="toggleConversationFavorite(conv, $event)">&#9733;</button>
              </button>
            </div>
            <div *ngIf="pinnedConversations.length === 0" class="chat-history-empty">No favorites yet. Star a conversation to pin it here.</div>
          </div>

          <!-- Empty state -->
          <div *ngIf="historyFilter === 'all' && conversations.length === 0" class="chat-history-empty">
            No conversations yet. Ask a question to get started.
          </div>
        </div>
      </div>

      <!-- Message list -->
      <div *ngFor="let msg of messages; let i = index" class="chat-message" [class.user]="msg.role === 'user'" [class.assistant]="msg.role === 'assistant'">
        <!-- Avatar (fullscreen only) -->
        <div *ngIf="isFullscreen" class="chat-avatar">
          <mat-icon *ngIf="msg.role === 'assistant'" svgIcon="genai"></mat-icon>
          <mat-icon *ngIf="msg.role === 'user'">person</mat-icon>
        </div>
        <div class="chat-msg-body">
          <!-- Assistant inline icon (side panel) -->
          <div *ngIf="msg.role === 'assistant' && !isFullscreen" class="chat-assistant-label">
            <mat-icon svgIcon="genai" class="chat-assistant-icon"></mat-icon>
          </div>
          <div class="chat-bubble-row" [class.user-row]="msg.role === 'user'">
            <button *ngIf="msg.role === 'user' && i === 0 && !isLoading"
                    class="chat-rerun-btn"
                    (click)="rerunQuery()"
                    matTooltip="Re-run this query with fresh data">
              <mat-icon>refresh</mat-icon>
            </button>
            <div class="chat-bubble">
            <div class="chat-bubble-content" [innerHTML]="msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content"></div>

            <!-- Structured data: Table -->
            <div *ngIf="msg.structured_data?.type === 'table'" class="chat-table-wrapper">
              <div *ngIf="msg.structured_data.title" class="chat-table-title">{{msg.structured_data.title}}</div>
              <table class="chat-md-table">
                <thead>
                  <tr>
                    <th *ngFor="let col of msg.structured_data.columns">{{col}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of msg.structured_data.rows">
                    <td *ngFor="let cell of row">{{cell}}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Structured data: Photos -->
            <div *ngIf="msg.structured_data?.type === 'photo_gallery'" class="chat-photo-gallery">
              <div class="chat-photo-grid">
                <img *ngFor="let photo of msg.structured_data.photos"
                     [src]="photo.thumb_url"
                     [alt]="photo.caption || ''"
                     (click)="openPhoto(photo, msg.structured_data.photos)"
                     class="chat-photo-thumb">
              </div>
              <div class="chat-photo-count" *ngIf="msg.structured_data.photos?.length > 0">
                {{msg.structured_data.photos.length}} photo{{msg.structured_data.photos.length !== 1 ? 's' : ''}} — click to view
              </div>
            </div>

            <!-- Structured data: Chart -->
            <app-chat-chart
              *ngIf="msg.structured_data?.type === 'chart'"
              [chartData]="msg.structured_data">
            </app-chat-chart>

            <!-- Download actions -->
            <div *ngIf="msg.role === 'assistant'" class="chat-download-actions">
              <button *ngIf="msg.uuid"
                      mat-stroked-button
                      class="chat-download-btn"
                      [disabled]="downloadingPdf === msg.uuid"
                      (click)="downloadPdf(msg.uuid, i)">
                <mat-spinner *ngIf="downloadingPdf === msg.uuid" diameter="14" strokeWidth="2"></mat-spinner>
                <mat-icon *ngIf="downloadingPdf !== msg.uuid">picture_as_pdf</mat-icon>
                PDF
              </button>
              <button *ngIf="hasTable(msg)"
                      mat-stroked-button
                      class="chat-download-btn"
                      (click)="downloadCsv(msg, i)">
                <mat-icon>table_chart</mat-icon> CSV
              </button>
            </div>
          </div>
          </div>
          <div class="chat-timestamp">{{msg.created_at | date:'shortTime'}}</div>
        </div>
      </div>

      <!-- Streaming indicator -->
      <div *ngIf="isLoading" class="chat-message assistant">
        <div *ngIf="isFullscreen" class="chat-avatar">
          <mat-icon svgIcon="genai"></mat-icon>
        </div>
        <div class="chat-msg-body">
          <div *ngIf="!isFullscreen" class="chat-assistant-label">
            <mat-icon svgIcon="genai" class="chat-assistant-icon"></mat-icon>
          </div>
          <div class="chat-bubble">
            <div *ngIf="streamingText" class="chat-bubble-content" [innerHTML]="renderMarkdown(streamingText)"></div>
            <div *ngIf="!streamingText" class="chat-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Input area -->
  <div *ngIf="!needsProjectSelection && consentAccepted && projectResolved" class="chat-input-area">
    <div *ngIf="rateLimitMessage" class="chat-rate-limit">
      <mat-icon>schedule</mat-icon>
      <span>{{rateLimitMessage}}</span>
    </div>
    <div class="chat-input-inner">
      <div class="chat-input-field" data-testid="chat-input-field">
        <button class="chat-voice-btn"
                (click)="toggleVoice()"
                [class.recording]="isRecording"
                [disabled]="isLoading"
                [matTooltip]="isRecording ? 'Stop recording' : 'Voice input'">
          <mat-icon>{{ isRecording ? 'stop' : 'mic' }}</mat-icon>
        </button>
        <textarea class="chat-input"
               [(ngModel)]="inputText"
               (keydown)="onKeyDown($event)"
               (input)="autoGrow($event)"
               [placeholder]="isRecording ? 'Listening...' : 'Ask me anything'"
               [disabled]="isLoading || isRecording"
               rows="1"></textarea>
      </div>
      <button class="chat-send-btn"
              (click)="sendMessage()"
              [disabled]="!inputText?.trim() || isLoading">
        <mat-icon>arrow_upward</mat-icon>
      </button>
    </div>
    <div *ngIf="isFullscreen" class="chat-disclaimer">
      AI can make mistakes. Verify important data against your project records.
    </div>
  </div>
</div>
